import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { apiRequest } from '../utils/api';

interface AdminDisableUserToolProps {
  isLoggedIn: boolean;
  isAdmin: boolean;
  isMobile: boolean;
  show: boolean;
  setShow: (visible: boolean) => void;
}

interface AdminUser {
  _id: string;
  email: string;
  is_admin: boolean;
  disabled_at?: string | null;
  disabled_reason?: string | null;
  disabled_by_admin_email?: string | null;
  blocked?: boolean;
  created_at?: string | null;
}

interface AdminUsersResponse {
  users: AdminUser[];
}

const scrollPaneStyle: React.CSSProperties = {
  maxHeight: '60vh',
  overflowY: 'auto',
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const AdminDisableUserTool: React.FC<AdminDisableUserToolProps> = ({
  isLoggedIn,
  isAdmin,
  isMobile,
  show,
  setShow,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AdminUser[]>([]);
  const [disabledUsers, setDisabledUsers] = useState<AdminUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDisabledUsers, setIsLoadingDisabledUsers] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [targetUser, setTargetUser] = useState<AdminUser | null>(null);
  const [disableReason, setDisableReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const closeModal = useCallback(() => {
    setShow(false);
    setError(null);
    setSuccess(null);
    setTargetUser(null);
    setDisableReason('');
  }, [setShow]);

  const fetchDisabledUsers = useCallback(async () => {
    if (!isLoggedIn || !isAdmin) return;

    try {
      setIsLoadingDisabledUsers(true);
      const response = await apiRequest(
        '/api/admin/users/disabled?limit=50',
        { method: 'GET' },
      ) as AdminUsersResponse;
      setDisabledUsers(response.users || []);
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to load disabled users');
      setDisabledUsers([]);
    } finally {
      setIsLoadingDisabledUsers(false);
    }
  }, [isLoggedIn, isAdmin]);

  const runSearch = useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!isLoggedIn || !isAdmin) return;

    if (!trimmedQuery) {
      setSearchResults([]);
      setLastSearchQuery('');
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      setLastSearchQuery(trimmedQuery);
      const params = new URLSearchParams({ q: trimmedQuery, limit: '20' });
      const response = await apiRequest(
        `/api/admin/users/search?${params.toString()}`,
        { method: 'GET' },
      ) as AdminUsersResponse;
      setSearchResults(response.users || []);
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to search users');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [isLoggedIn, isAdmin]);

  useEffect(() => {
    if (!show) return;

    if (!isLoggedIn || !isAdmin || isMobile) {
      setShow(false);
      return;
    }

    fetchDisabledUsers();
  }, [fetchDisabledUsers, isAdmin, isLoggedIn, isMobile, setShow, show]);

  const refreshLists = useCallback(async () => {
    await fetchDisabledUsers();
    if (lastSearchQuery) {
      await runSearch(lastSearchQuery);
    }
  }, [fetchDisabledUsers, lastSearchQuery, runSearch]);

  const handleSearchSubmit = useCallback(async (event?: React.FormEvent) => {
    event?.preventDefault();
    await runSearch(searchInput);
  }, [runSearch, searchInput]);

  const handleDisableSubmit = useCallback(async () => {
    if (!targetUser) return;
    const reason = disableReason.trim();
    if (!reason) {
      setError('Disable reason is required.');
      return;
    }

    try {
      setBusyUserId(targetUser._id);
      setError(null);
      setSuccess(null);
      await apiRequest('/api/admin/users/disable', {
        method: 'POST',
        body: {
          user_id: targetUser._id,
          reason,
        },
      });
      setSuccess(`Disabled ${targetUser.email}.`);
      setTargetUser(null);
      setDisableReason('');
      await refreshLists();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to disable user');
    } finally {
      setBusyUserId(null);
    }
  }, [disableReason, refreshLists, targetUser]);

  const handleEnableUser = useCallback(async (user: AdminUser) => {
    if (!window.confirm(`Re-enable ${user.email}?`)) {
      return;
    }

    try {
      setBusyUserId(user._id);
      setError(null);
      setSuccess(null);
      await apiRequest('/api/admin/users/enable', {
        method: 'POST',
        body: { user_id: user._id },
      });
      setSuccess(`Re-enabled ${user.email}.`);
      await refreshLists();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to re-enable user');
    } finally {
      setBusyUserId(null);
    }
  }, [refreshLists]);

  const renderUserMeta = (user: AdminUser) => {
    const isDisabled = Boolean(user.disabled_at || user.blocked);
    return (
      <div className="small text-muted">
        <span>Created: {formatDate(user.created_at)}</span>
        {isDisabled && (
          <>
            <br />
            <span>
              Disabled: {formatDate(user.disabled_at)}
              {user.disabled_reason ? ` (${user.disabled_reason})` : ''}
            </span>
          </>
        )}
      </div>
    );
  };

  const renderUserBadges = (user: AdminUser) => (
    <div className="d-flex gap-1 flex-wrap">
      {user.is_admin && <Badge variant="warning" className="text-dark">Admin</Badge>}
      {Boolean(user.disabled_at) && <Badge variant="danger">Disabled</Badge>}
      {user.blocked && <Badge variant="dark">Blocked</Badge>}
    </div>
  );

  return (
    <>
      <Modal
        show={show && isLoggedIn && isAdmin && !isMobile}
        onHide={closeModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Admin: Disable users</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Row className="g-4">
            <Col lg={6}>
              <h5>Search users</h5>
              <p className="text-muted small mb-3">
                Search by email, then disable an account with an audit reason.
              </p>

              <Form onSubmit={handleSearchSubmit} className="mb-3">
                <InputGroup>
                  <Form.Control
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search by email"
                  />
                  <InputGroup.Append>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSearching || searchInput.trim().length < 2}
                    >
                      {isSearching ? <Spinner animation="border" size="sm" /> : 'Search'}
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </Form>

              <div style={scrollPaneStyle}>
                <ListGroup>
                  {searchResults.length === 0 && (
                    <ListGroup.Item className="text-muted">
                      {lastSearchQuery ? 'No matching users found.' : 'Search for a user by email.'}
                    </ListGroup.Item>
                  )}
                  {searchResults.map((user) => {
                    const isDisabled = Boolean(user.disabled_at || user.blocked);
                    const isAdminTarget = Boolean(user.is_admin);
                    return (
                      <ListGroup.Item key={user._id}>
                        <div className="d-flex align-items-start justify-content-between gap-3">
                          <div>
                            <strong>{user.email}</strong>
                            {renderUserBadges(user)}
                            {renderUserMeta(user)}
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={isDisabled || isAdminTarget || busyUserId === user._id}
                            title={isAdminTarget ? 'Admin accounts cannot be disabled' : undefined}
                            onClick={() => {
                              setTargetUser(user);
                              setDisableReason('');
                              setError(null);
                              setSuccess(null);
                            }}
                          >
                            {busyUserId === user._id ? <Spinner animation="border" size="sm" /> : 'Disable'}
                          </Button>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              </div>
            </Col>

            <Col lg={6}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h5 className="mb-0">Currently disabled</h5>
                  <div className="text-muted small">Disabled or blocked accounts, newest first.</div>
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={fetchDisabledUsers}
                  disabled={isLoadingDisabledUsers}
                >
                  {isLoadingDisabledUsers ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                </Button>
              </div>

              <div style={scrollPaneStyle}>
                <ListGroup>
                  {disabledUsers.length === 0 && (
                    <ListGroup.Item className="text-muted">
                      No disabled users.
                    </ListGroup.Item>
                  )}
                  {disabledUsers.map((user) => (
                    <ListGroup.Item key={user._id}>
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div>
                          <strong>{user.email}</strong>
                          {renderUserBadges(user)}
                          <div className="small text-muted">
                            Disabled: {formatDate(user.disabled_at)}
                            {user.disabled_by_admin_email ? ` by ${user.disabled_by_admin_email}` : ''}
                            <br />
                            Reason: {user.disabled_reason || 'Not provided'}
                          </div>
                        </div>
                        <Button
                          variant="outline-success"
                          size="sm"
                          disabled={busyUserId === user._id}
                          onClick={() => handleEnableUser(user)}
                        >
                          {busyUserId === user._id ? <Spinner animation="border" size="sm" /> : 'Re-enable'}
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(targetUser)}
        onHide={() => {
          setTargetUser(null);
          setDisableReason('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Disable user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Disable <strong>{targetUser?.email}</strong>? This blocks existing JWT sessions from
            high-risk authenticated endpoints.
          </p>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={disableReason}
              onChange={(event) => setDisableReason(event.target.value)}
              placeholder="Why is this account being disabled?"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setTargetUser(null);
              setDisableReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDisableSubmit}
            disabled={!disableReason.trim() || Boolean(targetUser && busyUserId === targetUser._id)}
          >
            {targetUser && busyUserId === targetUser._id ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Disable user'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminDisableUserTool;
